/**
 * Partnership React Query Hooks
 *
 * React Query hooks for company partnership operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type {
  CreatePartnershipDto,
  UpdatePartnershipDto,
} from '@lognai/plugin-admin';
import * as partnershipApi from '../api/partnerships';
import type {
  EnrichedPartnership,
  PartnershipQueryParams,
  PaginatedPartnershipList,
  AddPartnerUserDto,
} from '../api/partnerships';
import { partnershipKeys } from './query-keys';

/**
 * Get list of partnerships for a company
 */
export const usePartnerships = (
  companyId: string,
  params?: PartnershipQueryParams,
  options?: Omit<
    UseQueryOptions<PaginatedPartnershipList>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    queryKey: partnershipKeys.list(companyId, params),
    queryFn: () => partnershipApi.listPartnerships(companyId, params),
    enabled: !!companyId,
    ...options,
  });
};

/**
 * Get a single partnership by ID
 */
export const usePartnership = (
  companyId: string,
  partnershipId: string,
  options?: Omit<UseQueryOptions<EnrichedPartnership>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: partnershipKeys.detail(companyId, partnershipId),
    queryFn: () => partnershipApi.getPartnership(companyId, partnershipId),
    enabled: !!companyId && !!partnershipId,
    ...options,
  });
};

/**
 * Create a partnership
 */
export const useCreatePartnership = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<EnrichedPartnership, Error, CreatePartnershipDto>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (data: EnrichedPartnership, variables: CreatePartnershipDto, context: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto) => partnershipApi.createPartnership(companyId, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: partnershipKeys.lists(companyId) });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Add an existing user as a partner of the company (reuse via secondaryCompanyIds)
 */
export const useAddPartnerUser = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<Awaited<ReturnType<typeof partnershipApi.addPartnerUser>>, Error, AddPartnerUserDto>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: Awaited<ReturnType<typeof partnershipApi.addPartnerUser>>,
      variables: AddPartnerUserDto,
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto) => partnershipApi.addPartnerUser(companyId, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: partnershipKeys.lists(companyId) });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Update a partnership
 */
export const useUpdatePartnership = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<
      EnrichedPartnership,
      Error,
      { partnershipId: string; dto: UpdatePartnershipDto }
    >,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: EnrichedPartnership,
      variables: { partnershipId: string; dto: UpdatePartnershipDto },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ partnershipId, dto }) =>
      partnershipApi.updatePartnership(companyId, partnershipId, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: partnershipKeys.detail(companyId, variables.partnershipId),
      });
      queryClient.invalidateQueries({ queryKey: partnershipKeys.lists(companyId) });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Terminate a partnership
 */
export const useTerminatePartnership = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<void, Error, { partnershipId: string; reason?: string }>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: void,
      variables: { partnershipId: string; reason?: string },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ partnershipId, reason }) =>
      partnershipApi.terminatePartnership(companyId, partnershipId, reason),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: partnershipKeys.lists(companyId) });
      queryClient.invalidateQueries({
        queryKey: partnershipKeys.detail(companyId, variables.partnershipId),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};
